import {
  Accordion,
  BrandIcon,
  ChevronDownIcon,
  ClearIcon,
  FontSizeEnum,
  FontWeightEnum,
  IconButton,
  IconButtonColorEnum,
  IconButtonSizeEnum,
  IconSizeEnum,
  RelevantPeopleIcon,
  ScreenTextOcrIcon,
  STTIcon,
  SupportRadioIcon,
  SupportTvIcon,
  Typography,
  VideoCutsCortesIcon,
} from '@acceso/design-system';
import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CheckboxConfirmation } from '@/components/FormFields/CheckboxConfirmation';
import { CheckboxForm } from '@/components/FormFields/CheckboxForm';
import { InputDate } from '@/components/FormFields/InputDate';
import { InputSwitch } from '@/components/FormFields/InputSwitch';
import { InputText } from '@/components/FormFields/InputText';
import { RowContainer } from '@/components/FormFields/RowContainer';
import { SelectForm } from '@/components/FormFields/SelectForm';
import { SwitchEnrichment } from '@/components/FormFields/SwitchEnrichment';
import { PRIORITY } from '@/features/broadcast-schedule/constants';
import { useChannels } from '@/features/broadcasters/api/get-channels';
import { useAudienceProgramSourceOptions } from '@/features/broadcasters/hooks/useAudienceProgramSourceOptions';
import { ChannelMediaTypeEnum, ChannelTypeEnum } from '@/features/broadcasters/types';
import { useLanguages } from '@/features/languages/api/get-languages';
import { State } from '@/types';

import { useGetProgramsCategories } from '../../api/get-programs-categories';
import { useProgramClassificationOptions } from '../../hooks/useProgramClassificationOptions';
import { useProgramViewedPlaceOptions } from '../../hooks/useProgramViewedPlaceOptions';
import { TProgramForm } from '../../types';

type TProgramDrawerProps = {
  form: UseFormReturn<TProgramForm, unknown, undefined>;
  isInitializing?: boolean;

  /**
   * @default false
   */
  hideHeader?: boolean;
};
export function ProgramDrawer({ form, isInitializing, hideHeader = false }: TProgramDrawerProps) {
  const { t } = useTranslation();

  const [mediaType, setMediaType] = useState<string>('');
  const [scopeIn, setScopeIn] = useState<string>('');

  const [audienceName, setAudienceName] = useState('');
  const audienceProgramSourceId = useWatch({
    control: form.control,
    name: 'audience_program_source_id',
  });
  const includeAudienceProgramSourceIds = useMemo(
    () => (audienceProgramSourceId ? [audienceProgramSourceId] : undefined),
    [audienceProgramSourceId]
  );
  const audienceProgramSourceOptions = useAudienceProgramSourceOptions({
    scopeIn,
    mediaType,
    name: audienceName,
    includeIds: includeAudienceProgramSourceIds,
  });
  const { data: { data: channels = [] } = {} } = useChannels();
  const { data: languages = [] } = useLanguages();
  const { data: categories = [] } = useGetProgramsCategories();

  const channelId = useWatch({
    control: form.control,
    name: 'channel_id',
  });

  const link_enrichments = form.watch('parameters.link_with_channel_enrichments');

  const selectedChannel = useMemo(
    () => channels.find(channel => channel.id === channelId),
    [channels, channelId]
  );

  const resetEnrichments = () => {
    if (!link_enrichments) return;
    const ch = selectedChannel?.parameters?.enrichments;
    if (!ch) return;

    if (ch.advertising) {
      form.setValue('parameters.enrichments.advertising', true);
    }
    if (ch.logos) {
      form.setValue('parameters.enrichments.logos', true);
    }
    if (ch.ocr) {
      form.setValue('parameters.enrichments.ocr', true);
    }
    if (ch.peoples) {
      form.setValue('parameters.enrichments.peoples', true);
    }
    if (ch.stt) {
      form.setValue('parameters.enrichments.stt', true);
    }
  };

  useEffect(() => {
    if (selectedChannel && !isInitializing) {
      form.setValue('audience_program_source_id', '');
      form.setValue('uri', selectedChannel.uri);

      if (selectedChannel.parameters?.enrichments) {
        resetEnrichments();
      }

      setMediaType(selectedChannel.media_type);
      setScopeIn(selectedChannel.scope);
    }
  }, [selectedChannel, channelId]);

  const channelsOptions = useMemo(() => {
    return channels
      .filter(channel => channel.parent_channel_id)
      .map(newChannel => {
        return {
          value: newChannel.id,
          label:
            newChannel.type === ChannelTypeEnum.BACKUP
              ? newChannel.name + ' (' + newChannel.short_name + ') (Backup)'
              : newChannel.name + ' (' + newChannel.short_name + ')',
          image: newChannel.parameters?.logo_presigned_url
            ? newChannel.parameters?.logo_presigned_url
            : '/default-logo.svg',
          icon: newChannel.media_type.includes(ChannelMediaTypeEnum.TV)
            ? SupportTvIcon
            : SupportRadioIcon,
        };
      });
  }, [channels]);

  const languagueOptions = useMemo(
    () =>
      languages.map(language => ({
        label: language.name,
        value: language.id,
      })),
    [languages]
  );

  const categoriesOptions = useMemo(
    () =>
      categories.map(category => ({
        label: category.name,
        value: category.id,
      })),
    [categories]
  );

  const classificationsOptions = useProgramClassificationOptions();

  const viewedPlacesOptions = useProgramViewedPlaceOptions();

  const priorityOptions = useMemo(
    () => PRIORITY.map(priority => ({ label: priority.render, value: priority.value })),
    []
  );

  const [accordionsOpen, setAccordionsOpen] = useState<Set<string>>(new Set());

  const handleAccordionOpen = (id: string) => {
    setAccordionsOpen(prev => {
      if (prev.has(id)) {
        return new Set([...prev].filter(item => item !== id));
      }
      return new Set([...prev, id]);
    });
  };

  const handleToggleAllAccordions = () => {
    if (accordionsOpen.size === 3) {
      setAccordionsOpen(new Set());
    } else {
      setAccordionsOpen(new Set(['configuration', 'perfil', 'enrichement']));
    }
  };

  const isRadio = selectedChannel?.media_type === ChannelMediaTypeEnum.RADIO;

  useEffect(() => {
    if (link_enrichments) {
      resetEnrichments();
    }
  }, [link_enrichments]);

  return (
    <div className="flex flex-1 flex-col gap-3">
      {!hideHeader && (
        <div className="flex w-full justify-between">
          <Typography fontSize={FontSizeEnum.Xl} weight={FontWeightEnum.Semibold}>
            {t('Programa')}
          </Typography>
          <IconButton
            size={IconButtonSizeEnum.LG}
            color={IconButtonColorEnum.DEFAULT_REVERSE}
            onClick={handleToggleAllAccordions}
          >
            <ChevronDownIcon
              size={IconSizeEnum.SM}
              className={clsx(
                'animation-translate duration-700',
                accordionsOpen.size === 3 ? 'rotate-180' : ''
              )}
            />
          </IconButton>
        </div>
      )}
      <InputSwitch
        form={form}
        name="state"
        states={{
          active: State.Active,
          inactive: State.Inactive,
        }}
      />
      <InputText form={form} name="name" required label={t('Nombre')} />
      <InputText form={form} name="description" required label={t('Descripción')} />
      <InputText form={form} name="legacy_id" label={t('ID AV (Legacy)')} />
      <RowContainer>
        <SelectForm
          form={form}
          name="program_category_id"
          required
          label={t('Categoría')}
          options={categoriesOptions}
        />
        <SelectForm
          form={form}
          name="program_classification"
          required
          label={t('Clasificación')}
          options={classificationsOptions}
        />
      </RowContainer>
      <RowContainer>
        <InputDate
          form={form}
          name="start_datetime"
          required
          label={t('Fecha inicio')}
          showTimeField={false}
        />
        <InputDate
          form={form}
          name="end_datetime"
          required
          label={t('Fecha fin')}
          showTimeField={false}
        />
      </RowContainer>

      <Accordion
        title={t('Configuración')}
        isOpen={accordionsOpen.has('configuration')}
        onOpen={() => {
          handleAccordionOpen('configuration');
        }}
        lineSeparator
        classes={{
          header: '!pb-2',
          body: 'py-2 px-4',
        }}
      >
        <RowContainer>
          <SelectForm
            form={form}
            name="language_id"
            required
            label={t('Idioma emisión')}
            options={languagueOptions}
          />
          <SelectForm
            form={form}
            name="language_transcription_id"
            required
            label={t('Idioma visionado')}
            options={languagueOptions}
          />
        </RowContainer>
        {/* <RowContainer className="mt-4">
          <SelectForm
            form={form}
            name="state"
            required
            label={t('Servicio especial')}
            options={[]}
          />
          <InputText form={form} name="state" required label={t('Email servicio especial')} />
        </RowContainer> */}
      </Accordion>
      <Accordion
        title={t('Perfil del programa')}
        isOpen={accordionsOpen.has('perfil')}
        onOpen={() => {
          handleAccordionOpen('perfil');
        }}
        classes={{
          header: '!pb-2',
          body: 'py-2 px-4',
        }}
        lineSeparator
      >
        <RowContainer>
          <SelectForm
            form={form}
            name="channel_id"
            required
            label={t('Emisora')}
            options={channelsOptions}
          />
          <SelectForm
            form={form}
            name="priority"
            required
            label={t('Prioridad')}
            options={priorityOptions as unknown as { label: string; value: number }[]}
          />
        </RowContainer>
        <RowContainer className="mt-4">
          {/*           <SelectForm form={form} name="state" required label={t('Desconexión')} options={[]} />
           */}
          <SelectForm
            form={form}
            name="audience_program_source_id"
            label={t('ID audiencia')}
            options={audienceProgramSourceOptions}
            onFilterTextChange={setAudienceName}
            withFilter
          />
          <SelectForm
            form={form}
            name="program_viewed_place_id"
            label={t('Lugar de visionado')}
            options={viewedPlacesOptions}
          />
        </RowContainer>
        {/* <RowContainer className="mt-4">
          <CheckboxForm
            form={form}
            name="checkbox"
            label={t('Programa especial')}
            classname="!justify-center gap-2 h-full items-center"
          />
        </RowContainer> */}
      </Accordion>
      <Accordion
        title={t('Enriquecimiento')}
        isOpen={accordionsOpen.has('enrichement')}
        onOpen={() => {
          handleAccordionOpen('enrichement');
        }}
        classes={{
          header: '!pb-2',
          body: 'py-2 px-4',
        }}
        lineSeparator
      >
        <div className="mb-2 flex items-center justify-end gap-2">
          <SwitchEnrichment form={form} name="parameters.link_with_channel_enrichments" />

          <IconButton className="-mr-1" size={IconButtonSizeEnum.SM} onClick={resetEnrichments}>
            <ClearIcon size={IconSizeEnum.XS} />
          </IconButton>
        </div>

        <div className="flex flex-col gap-3">
          <CheckboxForm
            form={form}
            name="parameters.enrichments.ocr"
            label={t('Texto en pantalla (OCR)')}
            icon={<ScreenTextOcrIcon size={IconSizeEnum.MD} />}
            readOnly={link_enrichments && selectedChannel?.parameters?.enrichments?.ocr}
            disabled={isRadio}
          />
          <hr></hr>
          <CheckboxForm
            form={form}
            name="parameters.enrichments.stt"
            label={t('Activar transcripción de texto')}
            icon={<STTIcon size={IconSizeEnum.MD} />}
            readOnly={link_enrichments && selectedChannel?.parameters?.enrichments?.stt}
            mapChange={(onChange, event) => {
              if (event.currentTarget.value === 'true') {
                // @ts-ignore
                form.setValue('parameters.enrichments.advertising', false);
              }

              onChange(event);
            }}
          />
          <hr></hr>
          <CheckboxForm
            form={form}
            name="parameters.enrichments.logos"
            label={t('Localizar logos')}
            icon={<BrandIcon size={IconSizeEnum.MD} />}
            readOnly={link_enrichments && selectedChannel?.parameters?.enrichments?.logos}
            disabled={isRadio}
          />
          <hr></hr>
          <CheckboxForm
            form={form}
            name="parameters.enrichments.peoples"
            label={t('Personas de interés')}
            icon={<RelevantPeopleIcon size={IconSizeEnum.MD} />}
            readOnly={link_enrichments && selectedChannel?.parameters?.enrichments?.peoples}
            disabled={isRadio}
          />
          <hr></hr>
          <CheckboxConfirmation
            form={form}
            name="parameters.enrichments.advertising"
            label={t('Cortes automáticos')}
            icon={<VideoCutsCortesIcon size={IconSizeEnum.MD} />}
            readOnly={link_enrichments && selectedChannel?.parameters?.enrichments?.advertising}
          />
          <hr></hr>
          <CheckboxForm
            form={form}
            name="parameters.ai_clips_require_review"
            label={t('Los cortes automáticos requieren revisión')}
            icon={<VideoCutsCortesIcon size={IconSizeEnum.MD} />}
          />
        </div>
      </Accordion>
    </div>
  );
}
